import React from "react";
import { motion } from "framer-motion";
import { useTranslation } from "react-i18next";
import { LeaderboardEntry, Num } from "@uno/protocol";
import Avatar from "component/Ui/Avatar";
import LevelBadge from "component/Ui/LevelBadge";
import getPublicAssetUrl from "utils/PublicAsset";

type Props = {
    entry: LeaderboardEntry;
    isMe: boolean;
    index: number;
};

const LeaderboardRow: React.FC<Props> = ({ entry, isMe, index }) => {
    const { t } = useTranslation();
    const score = entry.totalScore;

    return (
        <motion.li
            initial={{ opacity: 0, y: 8 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: Math.min(index * 0.03, 0.3), duration: 0.25 }}
            className={`relative flex items-center gap-3 pl-4 pr-3 py-2.5 rounded-2xl border-2 transition-colors overflow-hidden ${
                isMe
                    ? "bg-amber-400/15 border-amber-400/70 shadow-[0_0_24px_rgba(251,191,36,0.25)]"
                    : "bg-white/5 border-white/10 hover:bg-white/[0.08]"
            }`}
        >
            {isMe && (
                <span
                    aria-hidden="true"
                    className="absolute left-0 top-0 bottom-0 w-1 bg-amber-400"
                />
            )}

            <span
                className="shrink-0 font-changa-one text-2xl leading-none text-white w-6 text-center"
                style={{
                    transform: "skewX(-10deg)",
                    transformOrigin: "center",
                    textShadow: "-1px -1px 0 #1e40af, 1px -1px 0 #1e40af, -1px 1px 0 #1e40af, 1px 1px 0 #1e40af, 0 2px 0 #172554",
                }}
            >
                {entry.rank}
            </span>

            <div className="relative hidden xs:block shrink-0">
                <Avatar
                    src={entry.avatar}
                    className={`w-9 h-9 ${isMe ? "ring-2 ring-amber-300 ring-offset-2 ring-offset-transparent rounded-full" : ""}`}
                />
                <LevelBadge level={entry.level} className="-bottom-0.5 right-0 w-3.5 h-3.5 text-[7px]" />
                {isMe && (
                    <span
                        className="absolute bottom-0 left-1/2 whitespace-nowrap text-[7px] uppercase tracking-wider font-extrabold text-amber-950 bg-amber-300 rounded-full px-1.5 py-0.5 shadow-[0_2px_4px_rgba(0,0,0,0.25)]"
                        style={{ transform: "translate(-50%, calc(50% - 1px))" }}
                    >
                        {t("leaderboard.you")}
                    </span>
                )}
            </div>

            <div className="flex-1 min-w-0 flex items-center gap-1.5">
                {entry.flag && (
                    <img
                        src={getPublicAssetUrl(`assets/flags/${entry.flag}.svg`)}
                        className="w-4 h-3 object-contain shrink-0"
                        alt=""
                        draggable={false}
                    />
                )}
                <span className={`font-changa-one text-sm truncate ${isMe ? "text-amber-100" : "text-white"}`}>
                    {entry.username}
                </span>
            </div>

            <div className="shrink-0">
                <p className="font-black tabular-nums text-lg leading-none text-white/70">
                    {Num.compact(score)}
                </p>
            </div>
        </motion.li>
    );
};

export default LeaderboardRow;
